import { Outlet, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import { CapabilityProvider } from './CapabilityProvider';
import AdminSidebar from './AdminSidebar';
import AdminErrorBoundary from './AdminErrorBoundary';
import { getUnreadCount } from '../../services/adminNotificationsService';
import { useCapabilities } from './CapabilityProvider';
import { clearCapabilityCache } from '../../services/capabilityService';
import { adminLogout } from '../../services/adminAuthService';
import { spacing } from '../admin/ui/tokens';
import AuthDebugPanel from './AuthDebugPanel';
import Icon from './ui/icons/Icon';
import ThemeToggle from './ui/ThemeToggle';
import DensityToggle from './ui/DensityToggle';
import HeaderBrand from './ui/HeaderBrand';
import '../../styles/tokens/clc.css'; /* Design tokens - must load first */
import './AdminLayout.css';
import '../../styles/admin-layout.css';
import '../../styles/admin-ui.css'; /* Global Admin UI - Visual Identity */

function isUuidLike(value) {
  return typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

function getAdminDisplayName(me) {
  if (!me) return 'Admin';

  const email = typeof me.email === 'string' ? me.email.trim() : '';
  if (email && !isUuidLike(email)) {
    const [local] = email.split('@');
    return local || email;
  }

  const username = typeof me.username === 'string' ? me.username.trim() : '';
  if (username && !isUuidLike(username)) return username;

  const actorId = typeof me.actorId === 'string' ? me.actorId.trim() : '';
  if (actorId && !isUuidLike(actorId)) return actorId;

  return 'Admin';
}

function getInitials(label) {
  if (!label) return 'A';
  const parts = String(label)
    .replace(/[@._-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return 'A';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function NotificationBell() {
  const navigate = useNavigate();
  const { has } = useCapabilities();
  
  const canReadAudit = has('admin:audit:read');
  const canReadIntegrations = has('admin:integrations:read');
  const canReadOwnerDashboard = has('admin:owner-dashboard:read');
  const canRead = canReadAudit || canReadIntegrations || canReadOwnerDashboard;
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef(0);
  const FETCH_THROTTLE_MS = 60000; // Max once per 60 seconds

  const loadUnreadCount = useCallback(async () => {
    // Throttle: don't fetch if last fetch was < 60s ago
    const now = Date.now();
    if (now - lastFetchRef.current < FETCH_THROTTLE_MS) {
      return;
    }
    
    try {
      setLoading(true);
      const count = await getUnreadCount();
      setUnreadCount(count);
      lastFetchRef.current = now;
    } catch (err) {
      // Fail-soft: set count to 0 on error, don't spam retries
      console.warn('Failed to load unread count (fail-soft):', err.message);
      setUnreadCount(0);
      lastFetchRef.current = now; // Update throttle to prevent immediate retry
    } finally {
      setLoading(false);
    }
  }, []);

  // Load once on mount, then poll every 60 seconds
  useEffect(() => {
    if (canRead) {
      loadUnreadCount();
      // Poll every 60 seconds (reduced from 30s)
      const interval = setInterval(() => {
        loadUnreadCount();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [canRead, loadUnreadCount]);
  
  // Note: Removed location.pathname useEffect - unread count doesn't need to refresh on every route change

  if (!canRead) return null;

  return (
    <button
      className="admin-notification-button"
      onClick={() => navigate('/admin/notifications')}
      title="Notifications"
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
    >
      <Icon name="notifications" size="md" />
      {unreadCount > 0 && (
        <span className="admin-notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
      )}
    </button>
  );
}

function AccountControls() {
  const { me } = useCapabilities();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  
  useEffect(() => {
    // Close menu when clicking outside
    const handleClickOutside = (e) => {
      if (menuOpen && !e.target.closest('[data-account-controls]')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);
  
  const handleSignOut = async () => {
    try {
      await adminLogout();
    } catch (error) {
      // Logout endpoint may not be available (dev headers mode) - that's ok
      console.log('Logout endpoint not available, clearing cache only');
    }
    
    // Clear capability cache
    clearCapabilityCache();
    
    // Redirect to login page
    window.location.href = '/admin/login';
  };
  
  const handleSwitchAccount = async () => {
    await handleSignOut();
    // After reload, user can log in with different account
  };
  
  if (!me) {
    return null; // Don't show if not loaded yet
  }

  const displayName = getAdminDisplayName(me);
  const initials = getInitials(displayName);
  
  return (
    <div className="admin-account-controls" data-account-controls>
      <div className="admin-account-summary" aria-label={`Signed in as ${displayName}`}>
        <span className="admin-account-avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="admin-account-name">{displayName}</span>
      </div>
      <div className="admin-account-dropdown">
        <button
          className="admin-account-trigger"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          aria-label="Account menu"
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          <Icon name="settings" size="md" />
        </button>
        {menuOpen && (
          <div className="admin-account-menu" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handleSwitchAccount}
              className="admin-account-menu-item"
              aria-label="Switch account"
            >
              Switch Account
            </button>
            <button
              onClick={handleSignOut}
              className="admin-account-menu-item"
              aria-label="Sign out"
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCompact, setSidebarCompact] = useState(false);

  // Calculate sidebar width based on state
  const sidebarWidth = sidebarCompact ? 64 : 280; /* 280px - authoritative width */

  return (
    <CapabilityProvider>
      <style>{`
        /* Global focus styles for accessibility */
        button:focus-visible,
        a:focus-visible,
        input:focus-visible,
        select:focus-visible,
        textarea:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
        /* Skip link focus */
        a[href="#main-content"]:focus {
          top: 0;
        }
        /* Sidebar width CSS variable - single source of truth */
        .admin-layout-root {
          --admin-sidebar-width: ${sidebarWidth}px;
        }
      `}</style>
      <div style={styles.container} className="admin-layout-root">
        {/* Skip to main content link for keyboard users */}
        <a 
          href="#main-content" 
          style={styles.skipLink}
          onFocus={(e) => {
            e.currentTarget.style.top = '0';
          }}
          onBlur={(e) => {
            e.currentTarget.style.top = '-40px';
          }}
        >
          Skip to main content
        </a>
        <>
          <AdminSidebar 
            sidebarOpen={sidebarOpen} 
            onClose={() => setSidebarOpen(false)}
            compact={sidebarCompact}
            onCompactChange={setSidebarCompact}
          />
          {sidebarOpen && (
            <div 
              className="admin-sidebar-overlay"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            />
          )}
        </>
        <div className="admin-content-wrapper">
          <header className="admin-layout-header">
            <div className="admin-header-left">
              <button
                className="admin-mobile-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle navigation"
                aria-expanded={sidebarOpen}
              >
                ☰
              </button>
              <HeaderBrand />
            </div>
            <div className="admin-header-right">
              <div className="admin-header-mode-group" aria-label="Display controls">
                <DensityToggle />
                <ThemeToggle />
              </div>
              <div className="admin-header-utility-cluster" aria-label="Account and notifications">
                <NotificationBell />
                <AccountControls />
              </div>
            </div>
          </header>
          <main id="main-content" style={styles.main} className="admin-main" role="main" aria-label="Admin panel main content">
            <AdminErrorBoundary>
              <Outlet />
            </AdminErrorBoundary>
          </main>
        </div>
      </div>
      <AuthDebugPanel />
      {/* Toast container will be added by toast context/provider when implemented */}
    </CapabilityProvider>
  );
}

const styles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: 'var(--color-bg-secondary)', /* Subtle app background */
  },
  // contentWrapper styles moved to CSS - uses --admin-sidebar-width variable
  skipLink: {
    position: 'absolute',
    top: '-40px',
    left: '0',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    padding: '0.5rem 1rem',
    textDecoration: 'none',
    zIndex: 10000,
    borderRadius: '0 0 var(--radius-sm) 0',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
  },
  skipLinkFocus: {
    top: '0',
  },
  main: {
    flex: 1,
    padding: 'var(--spacing-4) var(--spacing-6)', /* 16px top, 24px sides - optimized for above-fold */
    maxWidth: '1800px',
    width: '100%',
    margin: '0 auto',
    backgroundColor: 'transparent', /* Transparent to show app background for surface layering */
  },
};

// Responsive styles moved to admin-layout.css - uses CSS variables and proper breakpoints
